<template>
  <section class="wsh">
    <div class="wsh-shelves">
      <div class="wsh-row">
        <input :value="sheet.name" class="wsh-name" placeholder="Sheet name" @input="patch({ name: $event.target.value })" />
        <div class="wsh-types" role="group" aria-label="Chart type">
          <button v-for="t in chartTypes" :key="t.key" class="wsh-type" :class="{ 'is-on': (spec.type || 'bar') === t.key }" :aria-pressed="(spec.type || 'bar') === t.key" @click="patchSpec({ type: t.key })">
            <font-awesome-icon :icon="t.icon" />{{ t.label }}
          </button>
        </div>
        <div class="wsh-actions">
          <ExportMenu :exports="exports" :disabled="!result || loading" size="sm" />
          <button v-if="canAddToDashboard" class="wsh-action" :disabled="!dashboardWorkflowId || !result" :title="dashboardWorkflowId ? 'Place this chart on a dashboard in Insights' : 'Approve and run the workflow, and let the sheet save, to put it on a dashboard'" @click="dashboardOpen = true">
            <font-awesome-icon :icon="icons.dashboard" />Add to dashboard
          </button>
        </div>
      </div>
      <div class="wsh-row wsh-row-shelves">
        <div class="wsh-shelf" :class="{ 'is-wide': xFields.length > 1 }" @dragover.prevent @drop="dropField($event, 'x')">
          <span class="wsh-shelf-label"><font-awesome-icon :icon="icons.columns" />Columns</span>
          <span v-if="spec.type === 'kpi'" class="wsh-muted">not used for KPI</span>
          <template v-else-if="xFields.length">
            <template v-for="(name, i) in xFields" :key="name">
              <span v-if="i" class="wsh-sep" aria-hidden="true">›</span>
              <span class="wsh-pill">{{ name }}<button :title="`Remove ${name}`" @click="removeX(name)">×</button></span>
            </template>
            <span v-if="xFields.length < maxXFields" class="wsh-more" :title="`Drop up to ${maxXFields} fields to split the chart by each combination`">+</span>
          </template>
          <span v-else class="wsh-muted">drop a category or date</span>
        </div>
        <div class="wsh-shelf" @dragover.prevent @drop="dropField($event, 'measure')">
          <span class="wsh-shelf-label"><font-awesome-icon :icon="icons.rows" />Rows</span>
          <select :value="measure.agg" class="wsh-agg" @change="patchSpec({ measure: { ...measure, agg: $event.target.value } })">
            <option v-for="a in aggs" :key="a" :value="a">{{ a }}</option>
          </select>
          <span v-if="measure.column" class="wsh-pill">{{ measure.column }}<button @click="patchSpec({ measure: { agg: 'count' } })">×</button></span>
          <span v-else class="wsh-muted">{{ measure.agg === 'count' ? 'rows' : 'drop a number' }}</span>
        </div>
        <div class="wsh-shelf wsh-shelf-color" role="radiogroup" aria-label="Chart color">
          <span class="wsh-shelf-label"><font-awesome-icon :icon="icons.color" />Color</span>
          <button type="button" class="wsh-swatch is-auto" :class="{ 'is-on': !spec.color }" role="radio" :aria-checked="!spec.color" title="Auto" @click="patchSpec({ color: null })">A</button>
          <button
            v-for="c in palette"
            :key="c"
            type="button"
            class="wsh-swatch"
            :class="{ 'is-on': spec.color === c }"
            role="radio"
            :aria-checked="spec.color === c"
            :title="c"
            :style="{ background: c }"
            @click="patchSpec({ color: c })"
          ></button>
          <span v-if="!colorApplies" class="wsh-muted">bar &amp; line</span>
        </div>
      </div>
      <div class="wsh-row" @dragover.prevent @drop="dropField($event, 'filter')">
        <span class="wsh-shelf-label"><font-awesome-icon :icon="icons.filter" />Filters</span>
        <span v-for="(f, i) in spec.filters || []" :key="i" class="wsh-filter">
          <span class="wsh-mono">{{ f.column }}</span>
          <select :value="f.op" class="wsh-agg" @change="setFilterOp(i, $event.target.value)">
            <option v-for="op in ops" :key="op" :value="op">{{ op }}</option>
          </select>
          <input v-if="!nullOp(f.op)" :value="Array.isArray(f.value) ? f.value.join(', ') : f.value" class="wsh-val sensitive" @change="setFilter(i, { value: parseValue($event.target.value, f.op) })" />
          <button @click="removeFilter(i)">×</button>
        </span>
        <span v-if="!(spec.filters || []).length" class="wsh-muted">drop a field to filter</span>
        <span class="wsh-analytics" @dragover.prevent @drop.stop="dropAnalytic">
          <span v-if="spec.reference" class="wsh-chip" :class="{ 'is-off': !referenceShown }" :title="referenceShown ? '' : 'Reference lines show on bar and line charts'">
            {{ spec.reference.kind === 'average' ? 'Average line' : 'Line at' }}
            <input v-if="spec.reference.kind === 'constant'" :value="spec.reference.value" type="number" class="wsh-num" @change="patchSpec({ reference: { kind: 'constant', value: Number($event.target.value) } })" />
            <span v-if="!referenceShown" class="wsh-chip-note">bar &amp; line only</span>
            <button @click="patchSpec({ reference: null })">×</button>
          </span>
          <span v-if="spec.totals" class="wsh-chip">Totals<button @click="patchSpec({ totals: false })">×</button></span>
          <span v-if="spec.top" class="wsh-chip">Top <input :value="spec.top" type="number" min="1" max="50" class="wsh-num" @change="patchSpec({ top: Number($event.target.value) })" /><button @click="patchSpec({ top: null })">×</button></span>
        </span>
      </div>
    </div>

    <div ref="body" class="wsh-body">
      <div v-if="!ready" class="wsh-blank">
        <div class="wsh-ghost" aria-hidden="true">
          <span v-for="(h, i) in [46, 72, 58, 88, 64, 38, 76]" :key="i" :style="{ height: `${h}%` }"></span>
        </div>
        <p class="wsh-blank-title">Drag fields to build a chart</p>
        <p class="wsh-blank-text">Put a category or date on <strong>Columns</strong> and a number on <strong>Rows</strong>, or click fields in the Data panel. Add lines and totals from the Analytics tab.</p>
      </div>
      <div v-else-if="error" class="wsh-error-box">
        <font-awesome-icon :icon="icons.warn" />
        <span>{{ error }}</span>
      </div>
      <LoadingIndicator v-else-if="loading && !result" message="Drawing the chart…" />
      <template v-else-if="result">
        <div class="wsh-meta">
          <span class="wsh-source"><font-awesome-icon :icon="icons.source" />{{ result.source }}</span>
          <span v-if="asOf" class="wsh-asof" :title="'The output table was last built ' + asOf.replace('as of ', '') + '. Charts read it until the next run.'">{{ asOf }}</span>
          <span v-if="spec.totals && summary && summary.total !== null && spec.type !== 'kpi'" class="wsh-total">{{ summary.total_label }} <strong>{{ formatNumber(summary.total) }}</strong></span>
          <span v-if="coverage" class="wsh-coverage">{{ coverage }}<template v-if="spec.totals || spec.reference">; totals and average use all of them</template></span>
          <span v-if="loading" class="wsh-updating">Updating…</span>
        </div>
        <div v-if="spec.type === 'kpi'" class="wsh-kpi">
          <span class="wsh-kpi-value">{{ formatNumber(summary ? summary.total : null) }}</span>
          <span class="wsh-kpi-label">{{ measureLabel }}</span>
        </div>
        <div v-else-if="view.component === 'chart_v6'" class="wsh-chart sensitive" data-demo-blur>
          <Chart_V6 :spec="view.props.spec" />
        </div>
        <div v-else class="wsh-table">
          <table>
            <thead><tr><th v-for="c in view.props.columnDefs" :key="c.field">{{ c.headerName || c.field }}</th></tr></thead>
            <tbody>
              <tr v-for="(r, i) in view.props.rowData" :key="i">
                <td v-for="c in view.props.columnDefs" :key="c.field" class="sensitive">{{ r[c.field] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <AddToDashboardModal
      :visible="dashboardOpen"
      :workflow-id="dashboardWorkflowId"
      :sheet-id="sheet.id"
      :sheet-name="sheet.name"
      @close="dashboardOpen = false"
      @added="onAdded"
    />
  </section>
</template>

<script>
import axios from 'axios';
import {
  faArrowsLeftRight, faArrowsUpDown, faChartColumn, faChartLine, faChartPie, faCircleExclamation,
  faDatabase, faFilter, faHashtag, faPalette, faTable, faTableCellsLarge,
} from '@fortawesome/free-solid-svg-icons';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import AddToDashboardModal from './AddToDashboardModal.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import { exportElementPng, exportRowsCsv, fileSlug } from '@/utils/workflowExports';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { asOfText, coverageText, formatNumber, labeledRows, sheetViz, supportsReference } from '@/utils/sheetAnalytics';
import { CHART_PALETTE } from '@/utils/chartPalette';
import abortable from '@/mixins/abortable';

const CHART_URL = '/api/etl_module/data-workflows/chart/';
const SHEET_CHART_LIMIT = 50;
const OPS = ['=', '!=', '>', '>=', '<', '<=', 'LIKE', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];

export default {
  name: 'WorkflowSheet',
  components: { Chart_V6, LoadingIndicator, ExportMenu, AddToDashboardModal },
  mixins: [abortable],
  props: {
    sheet: { type: Object, required: true },
    source: { type: Object, default: null },
    dashboardWorkflowId: { type: Number, default: null },
  },
  emits: ['update', 'added-to-dashboard'],
  data() {
    return {
      result: null, loading: false, error: '', timer: null, dashboardOpen: false, maxXFields: 3, palette: CHART_PALETTE,
      chartTypes: [
        { key: 'bar', label: 'Bar', icon: faChartColumn }, { key: 'line', label: 'Line', icon: faChartLine },
        { key: 'donut', label: 'Donut', icon: faChartPie }, { key: 'table', label: 'Table', icon: faTable },
        { key: 'kpi', label: 'KPI', icon: faHashtag },
      ],
      icons: {
        columns: faArrowsLeftRight, rows: faArrowsUpDown, color: faPalette, filter: faFilter,
        warn: faCircleExclamation, source: faDatabase, dashboard: faTableCellsLarge,
      },
      aggs: ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'],
      ops: OPS,
    };
  },
  computed: {
    spec() {
      return this.sheet.spec || {};
    },
    colorApplies() {
      return ['bar', 'line'].includes(this.spec.type || 'bar');
    },
    xFields() {
      const x = this.spec.x;
      return Array.isArray(x) ? x.filter(Boolean) : (x ? [x] : []);
    },
    measure() {
      return this.spec.measure || { agg: 'count' };
    },
    measureLabel() {
      const m = this.measure;
      return m.column ? `${m.agg.replace('_', ' ')} of ${m.column}` : 'rows';
    },
    ready() {
      return !!(this.source && (this.spec.type === 'kpi' || this.xFields.length));
    },
    request() {
      const s = this.spec;
      const kpi = s.type === 'kpi';
      return {
        type: s.type || 'bar', x: kpi ? undefined : (this.xFields.length > 1 ? this.xFields : this.xFields[0]),
        measure: this.measure, filters: s.filters || [],
        limit: kpi ? undefined : SHEET_CHART_LIMIT, top: !kpi && s.top ? s.top : undefined,
      };
    },
    summary() {
      return (this.result && this.result.summary) || null;
    },
    coverage() {
      return coverageText(this.summary);
    },
    asOf() {
      return asOfText(this.result && this.result.as_of);
    },
    referenceShown() {
      return supportsReference(this.spec.type);
    },
    view() {
      return sheetViz(this.result, this.spec.reference, this.spec.color);
    },
    canAddToDashboard() {
      return canAccessFeature(FEATURES.MODULE_DATA_WORKFLOW_BUILDER);
    },
    fileBase() {
      return fileSlug(this.sheet.name || 'sheet');
    },
    exports() {
      const rows = labeledRows(this.result, this.xFields.join(' · '));
      const items = [
        { type: 'csv', action: () => exportRowsCsv(rows, `${this.fileBase}.csv`) },
        { type: 'excel', action: () => exportToExcel(rows, `${this.fileBase}.xlsx`, { sheetName: (this.sheet.name || 'Sheet').slice(0, 31) }) },
      ];
      if (this.spec.type !== 'table') {
        items.push({ type: 'chart', menuLabel: 'Image (PNG)', action: () => exportElementPng(this.$refs.body, `${this.fileBase}.png`) });
      }
      return items;
    },
    fetchKey() {
      return JSON.stringify([this.request, this.source]);
    },
  },
  watch: {
    fetchKey: { immediate: true, handler() { this.schedule(); } },
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    onAdded(page) {
      this.dashboardOpen = false;
      this.$emit('added-to-dashboard', page);
    },
    addX(name) {
      if (this.xFields.includes(name)) return null;
      const next = this.xFields.length >= this.maxXFields ? [...this.xFields.slice(0, -1), name] : [...this.xFields, name];
      return this.patchSpec({ x: next.length > 1 ? next : next[0] });
    },
    removeX(name) {
      const next = this.xFields.filter((n) => n !== name);
      this.patchSpec({ x: next.length > 1 ? next : (next[0] || null) });
    },
    patch(fields) {
      this.$emit('update', { ...this.sheet, ...fields });
    },
    patchSpec(fields) {
      this.patch({ spec: { ...this.spec, ...fields } });
    },
    applyAnalytic(kind) {
      if (kind === 'average') this.patchSpec({ reference: { kind: 'average' } });
      if (kind === 'constant') this.patchSpec({ reference: { kind: 'constant', value: 0 } });
      if (kind === 'totals') this.patchSpec({ totals: true });
      if (kind === 'top') this.patchSpec({ top: 10 });
    },
    addField(field) {
      const numeric = field.type === 'number';
      if (this.spec.type !== 'kpi' && !this.xFields.length && !numeric) return this.addX(field.name);
      if (numeric && !this.measure.column) return this.patchSpec({ measure: { agg: 'sum', column: field.name } });
      if (!this.xFields.length) return this.addX(field.name);
      return null;
    },
    dropField(ev, shelf) {
      const raw = ev.dataTransfer.getData('field');
      if (!raw) return;
      const f = JSON.parse(raw);
      if (shelf === 'x') this.addX(f.name);
      if (shelf === 'measure') {
        this.patchSpec({ measure: { agg: f.type === 'number' ? (this.measure.agg === 'count' ? 'sum' : this.measure.agg) : 'count_distinct', column: f.name } });
      }
      if (shelf === 'filter') this.patchSpec({ filters: [...(this.spec.filters || []), { column: f.name, op: '=', value: '' }] });
    },
    dropAnalytic(ev) {
      const kind = ev.dataTransfer.getData('analytics');
      if (kind) this.applyAnalytic(kind);
    },
    setFilter(i, fields) {
      const filters = [...(this.spec.filters || [])];
      filters[i] = { ...filters[i], ...fields };
      this.patchSpec({ filters });
    },
    setFilterOp(i, op) {
      const current = (this.spec.filters || [])[i] || {};
      const raw = Array.isArray(current.value) ? current.value.join(', ') : String(current.value ?? '');
      this.setFilter(i, { op, value: this.nullOp(op) ? undefined : this.parseValue(raw, op) });
    },
    removeFilter(i) {
      this.patchSpec({ filters: (this.spec.filters || []).filter((_, idx) => idx !== i) });
    },
    nullOp(op) { return op === 'IS NULL' || op === 'IS NOT NULL'; },
    parseValue(raw, op) {
      const one = (s) => { const n = Number(s); return s !== '' && !Number.isNaN(n) ? n : s; };
      return op === 'IN' || op === 'NOT IN' ? raw.split(',').map((s) => s.trim()).filter(Boolean).map(one) : one(raw);
    },
    formatNumber,
    schedule() {
      clearTimeout(this.timer);
      if (!this.ready) { this.result = null; return; }
      this.timer = setTimeout(this.load, 600);
    },
    async load() {
      const signal = this._renewAbortSignal('chart');
      this.loading = true;
      this.error = '';
      try {
        const body = { chart: this.request, ...this.source };
        const { data } = await axios.post(CHART_URL, body, { signal });
        if (data.error) { this.error = data.error; this.result = null; } else { this.result = data; }
      } catch (error) {
        if (this._isAborted(error)) return;
        this.error = error.response?.data?.error || 'Could not draw the chart.';
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.wsh { display: flex; flex-direction: column; min-height: 0; height: 100%; background: #FFFFFF; }
.wsh-shelves { padding: 8px 16px; border-bottom: 1px solid #E8E9E9; display: flex; flex-direction: column; gap: 8px; background: #FAFAFA; }
.wsh-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.wsh-name { border: 1px solid transparent; background: transparent; font-size: 14px; font-weight: 600; color: #1E1F21; padding: 4px 8px; border-radius: 8px; }
.wsh-name:hover { border-color: #E8E9E9; background: #FFFFFF; }
.wsh-name:focus { outline: 2px solid #4573D2; outline-offset: 4px; background: #FFFFFF; }
.wsh-actions { display: inline-flex; align-items: center; gap: 8px; }
.wsh-action { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #DDDDDD; background: #FFFFFF; color: #353537; border-radius: 6px; padding: 4px 10px; font-size: 11.5px; font-weight: 500; cursor: pointer; }
.wsh-action:hover:not(:disabled) { border-color: #F76734; color: #9F4D30; }
.wsh-action:disabled { color: #9D9D9F; cursor: not-allowed; }
.wsh-action:focus-visible { outline: 2px solid #4573D2; outline-offset: 2px; }
.wsh-types { margin-left: auto; display: inline-flex; border: 1px solid #E8E9E9; border-radius: 8px; overflow: hidden; background: #FFFFFF; }
.wsh-type { display: inline-flex; align-items: center; gap: 4px; border: 0; border-right: 1px solid #E8E9E9; background: #FFFFFF; padding: 4px 8px; font-size: 12px; color: #353537; cursor: pointer; }
.wsh-type:last-child { border-right: 0; }
.wsh-type.is-on { background: #FFF7F4; color: #9F4D30; font-weight: 600; box-shadow: inset 0 -2px 0 #F76734; }
.wsh-type:focus-visible { outline: 2px solid #4573D2; outline-offset: -2px; }
.wsh-shelf { flex: 1; min-width: 200px; display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; border: 1px dashed #BBBCBC; border-radius: 8px; padding: 4px 8px; background: #FFFFFF; min-height: 32px; }
.wsh-shelf-label { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #626264; min-width: 72px; }
.wsh-pill, .wsh-chip, .wsh-filter { display: inline-flex; align-items: center; gap: 4px; border-radius: 9999px; padding: 2px 4px 2px 8px; font-size: 12px; }
.wsh-pill { background: #EBF4FF; color: #21426C; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap; }
.wsh-chip { background: #F1F9F0; color: #485446; border: 1px solid #D4ECD1; }
.wsh-filter { background: #F4F4F4; color: #353537; border: 1px solid #E8E9E9; }
.wsh-pill button, .wsh-chip button, .wsh-filter button { border: 0; background: transparent; color: inherit; cursor: pointer; font-size: 13px; line-height: 1; padding: 0 4px; }
.wsh-agg { border: 1px solid #E8E9E9; border-radius: 4px; font-size: 12px; padding: 1px 4px; background: #FFFFFF; color: #353537; }
.wsh-val { width: 96px; border: 1px solid #E8E9E9; border-radius: 4px; font-size: 12px; padding: 1px 4px; }
.wsh-num { width: 56px; border: 1px solid #D4ECD1; border-radius: 4px; font-size: 12px; padding: 0 4px; }
.wsh-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.wsh-analytics { margin-left: auto; display: inline-flex; gap: 8px; min-height: 24px; min-width: 120px; justify-content: flex-end; }
.wsh-muted { font-size: 12px; color: #626264; }
.wsh-row-shelves { align-items: stretch; flex-wrap: nowrap; }
.wsh-shelf.is-wide { flex: 2.2; }
.wsh-shelf-color { gap: 6px; }
.wsh-swatch { width: 18px; height: 18px; border-radius: 9999px; border: 2px solid #FFFFFF; box-shadow: 0 0 0 1px #DDDDDD; cursor: pointer; padding: 0; }
.wsh-swatch.is-on { box-shadow: 0 0 0 2px #1E1F21; }
.wsh-swatch.is-auto { background: #FFFFFF; font-size: 9px; font-weight: 700; color: #626264; line-height: 14px; }
.wsh-swatch:focus-visible { outline: 2px solid #4573D2; outline-offset: 2px; }
.wsh-sep { color: #9D9D9F; font-size: 12px; }
.wsh-more { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; border: 1px dashed #BBBCBC; border-radius: 9999px; font-size: 12px; color: #9D9D9F; }
.wsh-body { flex: 1; min-height: 0; overflow: auto; padding: 16px; display: flex; flex-direction: column; }
.wsh-blank { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.wsh-ghost { width: 240px; height: 88px; display: flex; align-items: flex-end; gap: 8px; padding: 0 8px 4px; border-bottom: 1px solid #DDDDDD; margin-bottom: 8px; }
.wsh-ghost span { flex: 1; border-radius: 4px 4px 0 0; background: #E8E9E9; }
.wsh-blank-title { margin: 0; font-size: 14px; font-weight: 600; color: #1E1F21; }
.wsh-blank-text { margin: 0; max-width: 460px; font-size: 12px; line-height: 1.5; color: #626264; }
.wsh-blank-text strong { color: #353537; }
.wsh-error-box { padding: 8px 16px; display: flex; align-items: center; gap: 8px; border: 1px solid #F28181; background: #FDECEC; border-radius: 8px; font-size: 12px; color: #D53D3D; }
.wsh-meta { display: flex; align-items: center; gap: 16px; font-size: 12px; color: #626264; margin-bottom: 8px; }
.wsh-source { display: inline-flex; align-items: center; gap: 8px; }
.wsh-total strong { color: #1E1F21; }
.wsh-updating { color: #4C6E97; }
.wsh-coverage { color: #626264; }
.wsh-asof { color: #626264; }
.wsh-chip.is-off { background: #F4F4F4; color: #626264; border-color: #E8E9E9; }
.wsh-chip-note { font-size: 11px; color: #626264; }
.wsh-kpi { display: flex; flex-direction: column; gap: 4px; padding: 20px 0; }
.wsh-kpi-value { font-size: 40px; font-weight: 600; line-height: 1; color: #1E1F21; font-variant-numeric: tabular-nums; }
.wsh-kpi-label { font-size: 13px; color: #626264; }
.wsh-chart { flex: 1; min-height: 220px; }
.wsh-table table { border-collapse: collapse; font-size: 12px; }
.wsh-table th { background: #F4F4F4; text-align: left; font-weight: 600; color: #1E1F21; padding: 8px 16px; border-bottom: 1px solid #E8E9E9; }
.wsh-table td { padding: 4px 16px; border-bottom: 1px solid #E8E9E9; color: #353537; }
</style>
